import { Navigate, Route, Routes } from "react-router-dom";
import { CrmThemeProvider } from "@NP-Webber/crm-kit";
import { AuthProvider } from "./auth/AuthProvider";
import { EntityLabelsProvider } from "./i18n/EntityLabelsProvider";
import { AppShell } from "./layout/AppShell";
import { CustomersListPage } from "./modules/customers/CustomersListPage";
import { OrganizationsListPage } from "./modules/organizations/OrganizationsListPage";
import { SettingsPage } from "./modules/settings/SettingsPage";
import { SettingsPermissionsPage } from "./modules/settings/SettingsPermissionsPage";
import { UsersListPage } from "./modules/users/UsersListPage";
import { TasksListPage } from "./modules/tasks/TasksListPage";
import { TeamMeetingsPage } from "./modules/meetings/TeamMeetingsPage";
import { StructurePage } from "./modules/financeStructure/StructurePage";
import { IncomeExpensesPage } from "./modules/cashLedger/IncomeExpensesPage";
import { SubscriptionsPaymentsPage } from "./modules/cashLedger/SubscriptionsPaymentsPage";
import { PersonalAreaPage } from "./modules/personal/PersonalAreaPage";
import { MailLogPage } from "./modules/mailLog/MailLogPage";
import { FormsListPage } from "./modules/forms/FormsListPage";
import { ChatPage } from "./modules/chat/ChatPage";
import { OrgGuidancePage } from "./modules/orgGuidance/OrgGuidancePage";
import { AutomationsPage } from "./next/automations/AutomationsPage";
import { MailingPage } from "./next/mailing/MailingPage";
import { SalesHomePage } from "./next/sales/SalesHomePage";
import { LeadsPage } from "./next/sales/LeadsPage";
import { DealsPage } from "./next/sales/DealsPage";
import { ListensPage } from "./next/sales/ListensPage";
import { YemotSendsPage } from "./next/sales/YemotSendsPage";
import { CommissionsPage } from "./next/sales/CommissionsPage";
import { SalesSettingsPage } from "./next/sales/SalesSettingsPage";
import { DataEntryAgentPage } from "./next/ai/dataEntry/DataEntryAgentPage";
import { DataAuditAgentRoute } from "./next/ai/dataAudit/DataAuditAgent";
import { DesignRoot } from "./next/design/DesignRoot";
import { LoginPage } from "./pages/LoginPage";
import { NoTenantAccessPage } from "./pages/NoTenantAccessPage";
import { PublicFormPage } from "./pages/PublicFormPage";
import { TenantHomePage } from "./pages/TenantHomePage";
import { TenantSelectPage } from "./pages/TenantSelectPage";
import {
  GuestOnly,
  NoAccessGate,
  RequireAuth,
  RequireTenant,
  TenantGate,
} from "./routes";

function App() {
  return (
    <CrmThemeProvider>
      <AuthProvider>
        <DesignRoot>
          <Routes>
            <Route path="/public/forms/:token" element={<PublicFormPage />} />

            <Route element={<GuestOnly />}>
              <Route path="/login" element={<LoginPage />} />
            </Route>

            <Route element={<RequireAuth />}>
              <Route element={<TenantGate />}>
                <Route path="/select-tenant" element={<TenantSelectPage />} />
              </Route>
              <Route element={<NoAccessGate />}>
                <Route path="/no-access" element={<NoTenantAccessPage />} />
              </Route>
              <Route element={<RequireTenant />}>
                <Route
                  element={
                    <EntityLabelsProvider>
                      <AppShell />
                    </EntityLabelsProvider>
                  }
                >
                  <Route path="/" element={<TenantHomePage />} />
                  <Route path="/organizations" element={<OrganizationsListPage />} />
                  <Route path="/customers" element={<CustomersListPage />} />
                  <Route path="/structure" element={<StructurePage />} />
                  <Route path="/subscriptions" element={<SubscriptionsPaymentsPage />} />
                  <Route path="/income-expenses" element={<IncomeExpensesPage section="ledger" />} />
                  <Route
                    path="/income-expenses/bank"
                    element={<IncomeExpensesPage section="bank" />}
                  />
                  <Route
                    path="/income-expenses/suppliers"
                    element={<IncomeExpensesPage section="suppliers" />}
                  />
                  <Route
                    path="/income-expenses/receipts"
                    element={<IncomeExpensesPage section="receipts" />}
                  />
                  <Route
                    path="/income-expenses/accounting"
                    element={<IncomeExpensesPage section="accounting" />}
                  />
                  <Route
                    path="/income-expenses/payment-requests"
                    element={<IncomeExpensesPage section="payment_requests" />}
                  />
                  <Route
                    path="/income-expenses/documents"
                    element={<IncomeExpensesPage section="documents" />}
                  />
                  <Route path="/users" element={<UsersListPage />} />
                  <Route path="/tasks" element={<TasksListPage />} />
                  <Route path="/meetings" element={<TeamMeetingsPage />} />
                  <Route path="/personal-area" element={<PersonalAreaPage />} />
                  <Route path="/org-guidance" element={<OrgGuidancePage />} />
                  <Route path="/mail-log" element={<MailLogPage />} />
                  <Route path="/forms" element={<FormsListPage />} />
                  <Route path="/chat" element={<ChatPage />} />
                  <Route path="/automations" element={<AutomationsPage />} />
                  <Route path="/mailing" element={<MailingPage />} />
                  <Route path="/mailing/:id" element={<MailingPage />} />
                  <Route path="/sales" element={<SalesHomePage />} />
                  <Route path="/sales/leads" element={<LeadsPage />} />
                  <Route path="/sales/leads/:id" element={<LeadsPage />} />
                  <Route path="/sales/deals" element={<DealsPage />} />
                  <Route path="/sales/deals/:id" element={<DealsPage />} />
                  <Route path="/sales/collections" element={<DealsPage mode="collections" />} />
                  <Route path="/sales/collections/:id" element={<DealsPage mode="collections" />} />
                  <Route path="/sales/listens" element={<ListensPage />} />
                  <Route path="/sales/sends" element={<YemotSendsPage />} />
                  <Route path="/sales/commissions" element={<CommissionsPage />} />
                  <Route path="/sales/settings" element={<SalesSettingsPage />} />
                  <Route path="/ai/data-entry" element={<DataEntryAgentPage />} />
                  <Route path="/ai/data-audit" element={<DataAuditAgentRoute />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route
                    path="/settings/permissions"
                    element={<SettingsPermissionsPage />}
                  />
                </Route>
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </DesignRoot>
      </AuthProvider>
    </CrmThemeProvider>
  );
}

export default App;
